// @ts-nocheck
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { SafeDictionary } from "@insite/client-framework/Common/Types";
// import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext"; // Starter Custom Code
import logger from "@insite/client-framework/Logger";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { canAddToCart, hasEnoughInventory } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import translate from "@insite/client-framework/Translate";
// import TextField, { TextFieldPresentationProps, TextFieldProps } from "@insite/mobius/TextField"; // Store Custom Code
// import React, { ChangeEvent, useEffect, useState } from "react"; // Starter Custom Code
import { connect } from "react-redux";
import { css } from "styled-components";

/* Starter Custom Code - Start */
import {
    HasProductContext,
    ProductContext,
    withProductContext,
} from "@insite/client-framework/Components/ProductContext";
import useExpandProps from "@toolkit/Framework/Common/Hooks/useExpandProps";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { useCanAddToCart, useHasEnoughInventory } from "@toolkit/Framework/Store/Data/Products/ProductsHooks";
import React, { ChangeEvent, useContext, useEffect, useState } from "react";

/* Store Custom Code - Start */
import QuantityAdjustButtons from "@store/Library/Components/QuantityAdjustButtons/QuantityAdjustButtons";
import TextField, { TextFieldPresentationProps, TextFieldProps } from "@store/MobiusOverrides/TextField/TextField";
/* Store Custom Code - End */

export interface ProductQuantityOrderedProps extends Omit<OwnProps, keyof TextFieldPresentationProps> {}

export const useShowProductQuantityOrdered = (
    configurationCompleted: boolean | undefined,
    variantSelectionCompleted: boolean | undefined,
    configurationSelection?: SafeDictionary<string>, // Store Custom Code
) => {
    const productContext = useContext(ProductContext);
    const { product, productInfo } = productContext;
    const hasEnoughInventory = useHasEnoughInventory(productContext);
    /* Store Custom Code - Start */
    const canAddToCart = useCanAddToCart(
        product,
        productInfo,
        configurationCompleted,
        variantSelectionCompleted,
        configurationSelection,
    );
    /* Store Custom Code - End */

    return !((!hasEnoughInventory && !productInfo.failedToLoadInventory) || !canAddToCart);
};
/* Starter Custom Code - End */

interface OwnProps extends TextFieldProps {
    configurationCompleted?: boolean;
    configurationSelection?: SafeDictionary<string>;
    variantSelectionCompleted?: boolean;
    labelOverride?: React.ReactNode;
    extendedStyles?: TextFieldPresentationProps;
    // Starter Custom Code
    overridedStyles?: TextFieldPresentationProps;
}

const mapStateToProps = (
    state: ApplicationState,
    props: {
        configurationCompleted?: boolean;
        variantSelectionCompleted?: boolean;
        configurationSelection?: SafeDictionary<string>;
    } & HasProductContext,
) => {
    return {
        canAddToCart: canAddToCart(
            state,
            props.productContext.product,
            props.productContext.productInfo,
            props.configurationCompleted,
            props.variantSelectionCompleted,
            props.configurationSelection,
        ),
        hasEnoughInventory: hasEnoughInventory(state, props.productContext),
    };
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & HasProductContext;

export const productQuantityOrderedStyles: TextFieldPresentationProps = {
    sizeVariant: "small",
    cssOverrides: {
        descriptionWrapper: css`
            ${x => x.theme.formField.defaultProps?.cssOverrides?.descriptionWrapper}
        `,
        formField: css`
            ${x => x.theme.formField.defaultProps?.cssOverrides?.formField}
            width: auto;
        `,
        formInputWrapper: css`
            ${x => x.theme.formField.defaultProps?.cssOverrides?.formInputWrapper}
        `,
        inputSelect: css`
            ${x => x.theme.formField.defaultProps?.cssOverrides?.inputSelect}
            /* Store Custom Code - Start */
            width: ${pxToRem(74)};
            padding: ${pxToRem(6)} ${pxToRem(22)} !important;
            text-align: center;
            /* Store Custom Code - End */
        `,
    },
};

/* Store Custom Code - Start */
const minQty = 0;
const maxQty = 999999;
/* Store Custom Code - End */

const ProductQuantityOrdered = ({
    hasEnoughInventory,
    productContext: {
        product: { id: productId },
        onQtyOrderedChanged,
        productInfo: { qtyOrdered, failedToLoadInventory },
    },
    labelOverride,
    extendedStyles,
    canAddToCart,
    configurationCompleted,
    configurationSelection,
    variantSelectionCompleted,
    overridedStyles, // Starter Custom Code
    onBlur = () => {}, // Starter Custom Code
    disabled, // Store Custom Code
    ...otherProps
}: Props) => {
    const styles = useExpandProps(productQuantityOrderedStyles, extendedStyles, overridedStyles); // Starter Custom Code
    const [productQty, setProductQty] = useState(qtyOrdered.toString());

    useEffect(() => {
        if (qtyOrdered === Number(productQty)) {
            return;
        }
        setProductQty(`${qtyOrdered || ""}`);
    }, [qtyOrdered]);

    /* Store Custom Code - Start */
    if (
        !useShowProductQuantityOrdered(
            configurationCompleted,
            variantSelectionCompleted,
            configurationSelection,
        )
    /* Store Custom Code - End */
    ) {
        return null;
    }

    const keyPressHandler = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.which === 13) {
            (event.target as any).blur();
        }
    };

    /* Store Custom Code - Start */
    const updateQty = (value: string) => {
        setProductQty(value);

        if (!onQtyOrderedChanged) {
            logger.warn(`There was no onQtyOrderedChanged passed to the ProductContext for ${productId}`);
            return;
        }

        const possibleNumber = Number(value);
        if (!Number.isNaN(possibleNumber)) {
            onQtyOrderedChanged(possibleNumber);
        }
    };

    const qtyChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
        updateQty(event.target.value);
    };

    const changeQuantityBy = (value: number) => {
        const currentQty = Number(productQty);
        const baseQty = Number.isNaN(currentQty) ? qtyOrdered : currentQty;
        const nextQty = Math.min(maxQty, Math.max(minQty, baseQty + value));

        updateQty(nextQty.toString());
    };

    const renderControlButtons = () => (
        <QuantityAdjustButtons
            decrementDisabled={disabled || Number(productQty || qtyOrdered) <= minQty}
            onDecrement={() => changeQuantityBy(-1)}
            incrementDisabled={disabled || Number(productQty || qtyOrdered) >= maxQty}
            onIncrement={() => changeQuantityBy(1)}
        />
    );
    /* Store Custom Code - End */

    return (
        <TextField
            label={labelOverride ?? translate("QTY")}
            value={productQty}
            type="number"
            min={minQty /* Store Custom Code */}
            max={maxQty /* Store Custom Code */}
            data-test-selector="product_qtyOrdered"
            {...otherProps}
            onKeyPress={keyPressHandler}
            onChange={qtyChangeHandler}
            onBlur={onBlur} // Starter Custom Code
            {...styles}
            // Store Custom Code
            renderControlButtons={renderControlButtons}
            disabled={disabled}
        />
    );
};

export default withProductContext(connect(mapStateToProps)(ProductQuantityOrdered));
